import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { bidCommitteeApi, type MeetingDocumentCategory } from '../../../api/bidCommittee';

// Query Keys
async function axiosBlobErrorMessage(e: unknown): Promise<string> {
  const err = e as { response?: { data?: unknown }; message?: string };
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const t = await data.text();
      const j = JSON.parse(t) as { error?: string };
      if (typeof j.error === 'string') return j.error;
    } catch {
      /* ignore */
    }
  }
  return err.message || 'Request failed';
}

function saveBlobAsFile(blob: Blob, filename: string, fallbackMime = 'application/octet-stream') {
  const mime = blob.type || fallbackMime;
  const fileBlob = new Blob([blob], { type: mime });
  const url = window.URL.createObjectURL(fileBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

export const queryKeys = {
  meetings: ['meetings'] as const,
  meeting: (id: string) => ['meeting', id] as const,
  meetingDocuments: (id: string) => ['meetingDocuments', id] as const,
  awardSheets: ['awardSheets'] as const,
  awardSheet: (id: string) => ['awardSheet', id] as const,
  agendaReturnForms: ['agendaReturnForms'] as const,
  agendaReturnForm: (id: string) => ['agendaReturnForm', id] as const,
  dashboardStats: ['dashboardStats'] as const,
};

// Meeting Hooks
export const useMeetings = () => {
  return useQuery({
    queryKey: queryKeys.meetings,
    queryFn: async () => {
      const res = await bidCommitteeApi.listMeetings();
      return res.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useMeeting = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.meeting(id!),
    queryFn: async () => {
      const res = await bidCommitteeApi.getMeeting(id!);
      return res.data;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useCreateMeeting = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await bidCommitteeApi.createMeeting(data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      notifications.show({
        title: 'Success',
        message: 'Meeting created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.message || 'Failed to create meeting',
        color: 'red',
      });
    },
  });
};

export const useUpdateMeeting = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await bidCommitteeApi.updateMeeting(id, data);
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      queryClient.invalidateQueries({ queryKey: queryKeys.meeting(variables.id) });
      notifications.show({
        title: 'Success',
        message: 'Meeting updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.message || 'Failed to update meeting',
        color: 'red',
      });
    },
  });
};

export const useScheduleMeeting = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id }: { id: string }) => bidCommitteeApi.scheduleMeeting(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      queryClient.invalidateQueries({ queryKey: queryKeys.meeting(variables.id) });
      notifications.show({
        title: 'Success',
        message: 'Meeting scheduled successfully. Invitations have been sent to all bid committee members.',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.message || 'Failed to schedule meeting',
        color: 'red',
      });
    },
  });
};

export const useStartMeeting = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id }: { id: string }) => bidCommitteeApi.startMeeting(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      queryClient.invalidateQueries({ queryKey: queryKeys.meeting(variables.id) });
      notifications.show({
        title: 'Success',
        message: 'Meeting started successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || error.response?.data?.error || 'Failed to start meeting',
        color: 'red',
      });
    },
  });
};

export const useFinishMeeting = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id }: { id: string }) => bidCommitteeApi.finishMeeting(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      notifications.show({
        title: 'Success',
        message: 'Meeting finished successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to finish meeting',
        color: 'red',
      });
    },
  });
};

// Attendance Hooks
export const useMarkAttendance = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ meetingId, data }: { meetingId: string; data: any }) => 
      bidCommitteeApi.markAttendance(meetingId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      queryClient.invalidateQueries({ queryKey: queryKeys.meeting(variables.meetingId) });
      notifications.show({
        title: 'Success',
        message: 'Attendance marked successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || error.response?.data?.error || 'Failed to mark attendance',
        color: 'red',
      });
    },
  });
};

// Single Source Hooks
export const useCreateSingleSource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: bidCommitteeApi.createSingleSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      notifications.show({
        title: 'Success',
        message: 'Single source form submitted successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to submit single source form',
        color: 'red',
      });
    },
  });
};

// Award Sheet Hooks
export const useAwardSheets = () => {
  return useQuery({
    queryKey: queryKeys.awardSheets,
    queryFn: async () => {
      const res = await bidCommitteeApi.listAwardSheets();
      return res.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useCreateAwardSheet = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: bidCommitteeApi.createAwardSheet,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.awardSheets });
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      queryClient.invalidateQueries({ queryKey: queryKeys.meeting(variables.meetingId) });
      queryClient.invalidateQueries({ queryKey: ['awardSheetsByMeeting', variables.meetingId] });
      notifications.show({
        title: 'Success',
        message: 'Award sheet created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to create award sheet',
        color: 'red',
      });
    },
  });
};

export const useUpdateAwardSheet = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => bidCommitteeApi.updateAwardSheet(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.awardSheets });
      queryClient.invalidateQueries({ queryKey: queryKeys.awardSheet(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      notifications.show({
        title: 'Success',
        message: 'Award sheet updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to update award sheet',
        color: 'red',
      });
    },
  });
};

export const useSendAwardSheetForApproval = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => bidCommitteeApi.sendAwardSheetForApproval(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.awardSheets });
      queryClient.invalidateQueries({ queryKey: queryKeys.awardSheet(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      notifications.show({
        title: 'Success',
        message: 'Award sheet sent for approval successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to send award sheet for approval',
        color: 'red',
      });
    },
  });
};

export const useSignAwardSheet = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      bidCommitteeApi.signAwardSheet(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.awardSheets });
      notifications.show({
        title: 'Success',
        message: 'Award sheet signed successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to sign award sheet',
        color: 'red',
      });
    },
  });
};

// Endorsement Hooks
export const useCreateEndorsement = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: bidCommitteeApi.createEndorsement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.awardSheets });
      notifications.show({
        title: 'Success',
        message: 'Endorsement created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to create endorsement',
        color: 'red',
      });
    },
  });
};

// Notification Hooks
export const useCreateNotification = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: bidCommitteeApi.createNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.awardSheets });
      notifications.show({
        title: 'Success',
        message: 'Notification sent successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to send notification',
        color: 'red',
      });
    },
  });
};

// Dashboard Stats Hook
export const useDashboardStats = () => {
  return useQuery({
    queryKey: queryKeys.dashboardStats,
    queryFn: bidCommitteeApi.getDashboardStats,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Recent Activities Hook
export const useRecentActivities = () => {
  return useQuery({
    queryKey: ['recentActivities'],
    queryFn: bidCommitteeApi.getRecentActivities,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Dashboard Filter Hooks
export const usePendingSignatures = () => {
  return useQuery({
    queryKey: ['pendingSignatures'],
    queryFn: bidCommitteeApi.getPendingSignatures,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useNeedsEndorsement = (enabled = true) => {
  return useQuery({
    queryKey: ['needsEndorsement'],
    queryFn: bidCommitteeApi.getNeedsEndorsement,
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled,
  });
};

export const useAwardSheet = (id: string) => {
  return useQuery({
    queryKey: queryKeys.awardSheet(id),
    queryFn: async () => {
      const res = await bidCommitteeApi.getAwardSheet(id);
      return res.data;
    },
    enabled: !!id,
  });
};

export const useAwardSheetsByMeeting = (meetingId: string | undefined) => {
  return useQuery({
    queryKey: ['awardSheetsByMeeting', meetingId],
    queryFn: async () => {
      if (!meetingId) return [];
      const res = await bidCommitteeApi.listAwardSheetsByMeeting(meetingId);
      return res.data;
    },
    enabled: !!meetingId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useBidCommitteeMeetings = () => {
  return useQuery({
    queryKey: queryKeys.meetings,
    queryFn: async () => {
      const res = await bidCommitteeApi.listMeetings();
      return res.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// CEO Endorsement Hooks
export const useCreateCEOEndorsement = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: bidCommitteeApi.createCEOEndorsement,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      queryClient.invalidateQueries({ queryKey: queryKeys.meeting(variables.meetingId) });
      queryClient.invalidateQueries({ queryKey: ['ceoEndorsementsByMeeting', variables.meetingId] });
      notifications.show({
        title: 'Success',
        message: 'CEO endorsement created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to create CEO endorsement',
        color: 'red',
      });
    },
  });
};

export const useUpdateCEOEndorsement = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => bidCommitteeApi.updateCEOEndorsement(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ceoEndorsement', variables.id] });
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      notifications.show({
        title: 'Success',
        message: 'CEO endorsement updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to update CEO endorsement',
        color: 'red',
      });
    },
  });
};

export const useCEOEndorsement = (id: string | undefined) => {
  return useQuery({
    queryKey: ['ceoEndorsement', id],
    queryFn: async () => {
      const res = await bidCommitteeApi.getCEOEndorsement(id!);
      return res.data;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useCEOEndorsementsByMeeting = (meetingId: string | undefined) => {
  return useQuery({
    queryKey: ['ceoEndorsementsByMeeting', meetingId],
    queryFn: async () => {
      if (!meetingId) return [];
      const res = await bidCommitteeApi.listCEOEndorsementsByMeeting(meetingId);
      return res.data;
    },
    enabled: !!meetingId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useSendCEOEndorsementForApproval = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => bidCommitteeApi.sendCEOEndorsementForApproval(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['ceoEndorsement', id] });
      queryClient.invalidateQueries({ queryKey: ['pendingCEOEndorsementApprovals'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      notifications.show({
        title: 'Success',
        message: 'CEO endorsement sent for approval successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to send endorsement for approval',
        color: 'red',
      });
    },
  });
};

export const useApproveCEOEndorsement = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { comments?: string } }) =>
      bidCommitteeApi.approveCEOEndorsement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingCEOEndorsementApprovals'] });
      queryClient.invalidateQueries({ queryKey: ['ceoEndorsement'] });
      notifications.show({
        title: 'Success',
        message: 'CEO endorsement approved successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to approve endorsement',
        color: 'red',
      });
    },
  });
};

export const useRejectCEOEndorsement = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { comments: string } }) =>
      bidCommitteeApi.rejectCEOEndorsement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingCEOEndorsementApprovals'] });
      queryClient.invalidateQueries({ queryKey: ['ceoEndorsement'] });
      notifications.show({
        title: 'Success',
        message: 'CEO endorsement rejected successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to reject endorsement',
        color: 'red',
      });
    },
  });
};

export const usePendingCEOEndorsementApprovals = () => {
  return useQuery({
    queryKey: ['pendingCEOEndorsementApprovals'],
    queryFn: async () => {
      const res = await bidCommitteeApi.getPendingCEOEndorsementApprovals();
      return res.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Award Sheet Approval Hooks
export const usePendingAwardSheetApprovals = () => {
  return useQuery({
    queryKey: ['pendingAwardSheetApprovals'],
    queryFn: async () => {
      const res = await bidCommitteeApi.getPendingAwardSheetApprovals();
      return res.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useApproveAwardSheetApproval = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { comments?: string } }) =>
      bidCommitteeApi.approveAwardSheetApproval(id, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['pendingAwardSheetApprovals'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.awardSheets });
      const awardSheetId = res.data?.approval?.awardSheetId;
      if (awardSheetId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.awardSheet(awardSheetId) });
      } else {
        queryClient.invalidateQueries({ queryKey: ['awardSheet'] });
      }
      notifications.show({
        title: 'Success',
        message: 'Award sheet approved successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to approve award sheet',
        color: 'red',
      });
    },
  });
};

export const useRejectAwardSheetApproval = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { comments: string } }) =>
      bidCommitteeApi.rejectAwardSheetApproval(id, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['pendingAwardSheetApprovals'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.awardSheets });
      const awardSheetId = res.data?.approval?.awardSheetId;
      if (awardSheetId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.awardSheet(awardSheetId) });
      } else {
        queryClient.invalidateQueries({ queryKey: ['awardSheet'] });
      }
      notifications.show({
        title: 'Success',
        message: 'Award sheet rejected successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to reject award sheet',
        color: 'red',
      });
    },
  });
};

// Agenda return form hooks
export const useAgendaReturnFormsByMeeting = (meetingId: string | undefined) => {
  return useQuery({
    queryKey: ['agendaReturnFormsByMeeting', meetingId],
    queryFn: async () => {
      if (!meetingId) return [];
      const res = await bidCommitteeApi.listAgendaReturnFormsByMeeting(meetingId);
      return res.data;
    },
    enabled: !!meetingId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useAgendaReturnForm = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.agendaReturnForm(id!),
    queryFn: async () => {
      const res = await bidCommitteeApi.getAgendaReturnForm(id!);
      return res.data;
    },
    enabled: !!id,
  });
};

export const useCreateAgendaReturnForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bidCommitteeApi.createAgendaReturnForm,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agendaReturnForms });
      queryClient.invalidateQueries({ queryKey: queryKeys.meeting(variables.meetingId) });
      queryClient.invalidateQueries({ queryKey: ['agendaReturnFormsByMeeting', variables.meetingId] });
      notifications.show({ title: 'Success', message: 'Agenda return form created', color: 'green' });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to create return form',
        color: 'red',
      });
    },
  });
};

export const useUpdateAgendaReturnForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import('../../../api/bidCommittee').AgendaReturnFormRequest> }) =>
      bidCommitteeApi.updateAgendaReturnForm(id, data),
    onSuccess: (_, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agendaReturnForm(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.agendaReturnForms });
      if (data.meetingId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.meeting(data.meetingId) });
      }
      notifications.show({ title: 'Success', message: 'Return form updated', color: 'green' });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update return form',
        color: 'red',
      });
    },
  });
};

export const useSendAgendaReturnFormForApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => bidCommitteeApi.sendAgendaReturnFormForApproval(id),
    onSuccess: (res) => {
      const form = res.data;
      queryClient.invalidateQueries({ queryKey: queryKeys.agendaReturnForm(form.id) });
      queryClient.invalidateQueries({ queryKey: ['agendaReturnFormsByMeeting', form.meetingId] });
      notifications.show({ title: 'Success', message: 'Sent for approval', color: 'green' });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to send for approval',
        color: 'red',
      });
    },
  });
};

export const useGenerateAgendaReturnFormPdf = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await bidCommitteeApi.generateAgendaReturnFormPdf(id);
      return res.data as Blob;
    },
    onSuccess: (blob, id) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ARF-${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      notifications.show({ title: 'PDF', message: 'Download started', color: 'green' });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to generate PDF',
        color: 'red',
      });
    },
  });
};

export const useGenerateAwardSheetPdf = () => {
  return useMutation({
    mutationFn: async ({ id, fileName }: { id: string; fileName?: string }) => {
      const res = await bidCommitteeApi.generateAwardSheetPdf(id);
      return { blob: res.data as Blob, fileName: fileName ?? `AS-${id}.pdf` };
    },
    onSuccess: ({ blob, fileName }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      notifications.show({ title: 'PDF', message: 'Download started', color: 'green' });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to generate PDF',
        color: 'red',
      });
    },
  });
};

export const usePendingAgendaReturnFormApprovals = () => {
  return useQuery({
    queryKey: ['pendingAgendaReturnFormApprovals'],
    queryFn: async () => {
      const res = await bidCommitteeApi.getPendingAgendaReturnFormApprovals();
      return res.data;
    },
    staleTime: 2 * 60 * 1000,
  });
};

export const useApproveAgendaReturnFormApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: { comments?: string } }) =>
      bidCommitteeApi.approveAgendaReturnFormApproval(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingAgendaReturnFormApprovals'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.agendaReturnForms });
      notifications.show({ title: 'Success', message: 'Vote recorded', color: 'green' });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to approve',
        color: 'red',
      });
    },
  });
};

export const useRejectAgendaReturnFormApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { comments: string } }) =>
      bidCommitteeApi.rejectAgendaReturnFormApproval(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingAgendaReturnFormApprovals'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.agendaReturnForms });
      notifications.show({ title: 'Success', message: 'Vote recorded', color: 'green' });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to reject',
        color: 'red',
      });
    },
  });
};

export const useChairApproveAgendaReturnForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: { signaturePngBase64?: string } }) =>
      bidCommitteeApi.chairApproveAgendaReturnForm(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agendaReturnForm(id) });
      queryClient.invalidateQueries({ queryKey: ['pendingAgendaReturnFormApprovals'] });
      notifications.show({ title: 'Success', message: 'Chair approval recorded', color: 'green' });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to approve as chair',
        color: 'red',
      });
    },
  });
};

export const useMeetingDocuments = (meetingId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.meetingDocuments(meetingId!),
    queryFn: async () => {
      const res = await bidCommitteeApi.getMeetingDocuments(meetingId!);
      return res.data;
    },
    enabled: !!meetingId,
    staleTime: 60 * 1000,
  });
};

export const useDownloadMeetingDocumentsZip = () => {
  return useMutation({
    mutationFn: async ({
      meetingId,
      category,
      fileName,
    }: {
      meetingId: string;
      category?: MeetingDocumentCategory;
      fileName: string;
    }) => {
      const res = await bidCommitteeApi.downloadMeetingDocumentsZip(meetingId, category);
      return { blob: res.data as Blob, fileName };
    },
    onSuccess: ({ blob, fileName }) => {
      saveBlobAsFile(blob, fileName, 'application/zip');
      notifications.show({ title: 'Download', message: 'Archive saved', color: 'green' });
    },
    onError: async (error: unknown) => {
      notifications.show({
        title: 'Error',
        message: await axiosBlobErrorMessage(error),
        color: 'red',
      });
    },
  });
};

export const useDownloadAgendaTenderDocument = () => {
  return useMutation({
    mutationFn: async ({ documentId, fileName }: { documentId: string; fileName: string }) => {
      const res = await bidCommitteeApi.downloadAgendaTenderDocument(documentId);
      return { blob: res.data as Blob, fileName };
    },
    onSuccess: ({ blob, fileName }) => {
      saveBlobAsFile(blob, fileName);
    },
    onError: async (error: unknown) => {
      notifications.show({
        title: 'Error',
        message: await axiosBlobErrorMessage(error),
        color: 'red',
      });
    },
  });
};

export const useDownloadMeetingDocument = () => {
  return useMutation({
    mutationFn: async ({
      meetingId,
      documentKey,
      fileName,
    }: {
      meetingId: string;
      documentKey: string;
      fileName: string;
    }) => {
      const res = await bidCommitteeApi.downloadMeetingDocument(meetingId, documentKey);
      return { blob: res.data as Blob, fileName };
    },
    onSuccess: ({ blob, fileName }) => {
      saveBlobAsFile(blob, fileName);
      notifications.show({ title: 'Download', message: 'File saved', color: 'green' });
    },
    onError: async (error: unknown) => {
      notifications.show({
        title: 'Error',
        message: await axiosBlobErrorMessage(error),
        color: 'red',
      });
    },
  });
};

export const useSecretarySignAgendaReturnForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: { signaturePngBase64?: string } }) =>
      bidCommitteeApi.secretarySignAgendaReturnForm(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agendaReturnForm(id) });
      queryClient.invalidateQueries({ queryKey: ['pendingAgendaReturnFormApprovals'] });
      notifications.show({ title: 'Success', message: 'Secretary signature recorded', color: 'green' });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to sign as secretary',
        color: 'red',
      });
    },
  });
};